import { TouchableOpacity, View } from 'react-native'
import { Divider } from '@ui-kitten/components'
import React from 'react'
import { useNavigation } from '@react-navigation/core'
import { useSelector } from 'react-redux'
import { Icon, Pill, Text } from '@components/base'
import { SubscriptionFields } from '@screens/Marketplace/components/ProgramDetails/SubscriptionFields.tsx'
import { Fields } from '@screens/Marketplace/components/ProgramDetails/ProductSummaryFields.tsx'
import {
  Interval,
  ProductMin,
  EhrAppointmentSchedulingRuleDef,
  Subscription,
  SubscriptionStatus,
  UiPillType,
} from '@src/types.ts'
import { PaymentMethodCard } from '@screens/Marketplace/components/checkout/PaymentMethodCard.tsx'
import { AddressForm, AddressType } from '@screens/Marketplace/components/checkout/AddressForm.tsx'
import {
  formatCurrencyAmount,
  getSubscriptionTagDescriptor,
  isFreeSoftwareOnlySubscription,
  isTrialUpgradable,
} from '@screens/Marketplace/utils/utils.ts'
import { StyleService, useStyleSheet } from '@style/service.ts'
import { userSelector } from '@selectors/app.ts'
import { useProducts } from '@screens/Marketplace/utils/hooks.ts'
import { useCanChangeBillingCycle } from '@screens/Marketplace/components/BillingCycle/useCanChangeBillingCycle.ts'
import { openUrl } from '@utils'
import { STRIPE_VIEW_INVOICES } from '@screens/Marketplace/constants/constants.ts'
import { CheckoutKind } from '@screens/Marketplace/types/types.ts'
import { useLazyScreenPreload } from '@src/navigation/utils'
import { ProgramDetailsActions } from './ProgramDetailsActions'

export const ProgramDetailsContent = ({
  showBillingFields,
  title,
  manageButtonLabel,
  onManageButtonPress,
  subscription,
}: {
  showBillingFields: boolean
  title?: string
  manageButtonLabel?: string
  onManageButtonPress?: () => void
  subscription: Pick<
    Subscription,
    | 'id'
    | 'commitmentEndAt'
    | 'status'
    | 'resetBillingCycleAnchorAt'
    | 'currentPeriodEndAt'
    | 'cancelAt'
    | 'paused'
    | 'price'
    | 'discountedPrice'
  > & {
    primaryProduct: Pick<
      ProductMin,
      'id' | 'uhc' | 'requiresShipping' | 'title' | 'key' | 'uncancelable' | 'billingDateChangeable'
    > & {
      ehrSchedulingRule: Pick<EhrAppointmentSchedulingRuleDef, 'count'>
      interval: Pick<Interval, 'suffix' | 'description'>
    }
  }
}) => {
  const styles = useStyleSheet(themedStyle)
  const navigation = useNavigation()
  const user = useSelector(userSelector)
  const canChangeBillingCycle = useCanChangeBillingCycle(subscription)
  const { products, upcomingSubscriptionPhases } = useProducts()

  const { address, paymentMethod, email } = user || {}

  const onEditPaymentMethodPress = () => {
    navigation.navigate('CreatePaymentMethod')
  }

  const { isTrial, pillLabel, pillStatus } = getSubscriptionTagDescriptor(subscription)
  const { requiresShipping } = subscription.primaryProduct
  const displayPrice = subscription.discountedPrice

  const addressType = requiresShipping ? AddressType.Shipping : AddressType.Billing

  useLazyScreenPreload('ChangeBillingCycleModal', {
    shouldPreload: canChangeBillingCycle && showBillingFields,
  })

  const onEditAddressPress = () => {
    navigation.navigate('ChangeAddress', { addressType })
  }

  const onViewStripeInvoicesPress = () => {
    openUrl(`${STRIPE_VIEW_INVOICES}${encodeURIComponent(email || '')}`)
  }

  const onChangeBillingCyclePress = () => {
    navigation.navigate('ChangeBillingCycleModal', { subscriptionId: subscription.id })
  }

  const showTrialUpgrade = isTrialUpgradable(subscription)

  const onTrialUpgradePress = () => {
    const product = products.find((product) => product.id === subscription.primaryProduct.id)!

    navigation.navigate('SubscriptionCheckout', {
      subscriptionId: subscription.id,
      product,
      trialEndsAt: subscription.cancelAt,
      checkoutKind: CheckoutKind.UpgradeTrial,
    })
  }

  return (
    <View>
      {title && (
        <View style={styles.field}>
          <Text type="regular" bold>
            {title}
          </Text>
        </View>
      )}

      <View style={styles.field}>
        <View style={styles.fieldSection}>
          <Text type="small" lineSpacing="none" style={styles.label}>
            Current Plan
          </Text>
          <Text type="large" bold lineSpacing="none">
            {subscription.primaryProduct.title}
          </Text>
        </View>
        <Pill text={pillLabel} status={pillStatus} />
      </View>

      {showBillingFields && (
        <View style={styles.field}>
          <Text type="regular" lineSpacing="none" style={styles.billingInterval}>
            {isTrial ? 'Complimentary access' : subscription.primaryProduct.interval.description}
          </Text>
          <Text type="large" bold>
            ${formatCurrencyAmount(displayPrice)}
          </Text>
        </View>
      )}

      <SubscriptionFields
        subscription={subscription}
        mode="full"
        fields={[
          Fields.daysLeft,
          ...(subscription.status !== SubscriptionStatus.PastDue ? [Fields.commitmentEnds] : []),
          Fields.subscriptionEnds,
          Fields.nextBillingDate,
          Fields.resumesAt,
          Fields.callUsed,
        ]}
      />

      {upcomingSubscriptionPhases.length > 0 &&
        upcomingSubscriptionPhases.map((phase) => (
          <View style={styles.field} key={phase.id}>
            <View style={styles.fieldSection}>
              <Text type="small" lineSpacing="none" style={styles.label}>
                Upcoming Plan
              </Text>
              <Text type="large" bold lineSpacing="none">
                {phase.primaryProduct!.title}
              </Text>
            </View>
            <Pill text="Scheduled to Start" status={UiPillType.Success} />
          </View>
        ))}

      {canChangeBillingCycle && showBillingFields && (
        <>
          <View style={styles.fieldDividerInset}>
            <Divider />
          </View>
          <TouchableOpacity
            onPress={onChangeBillingCyclePress}
            accessibilityLabel="Change shipping & billing Date"
            style={[styles.field, styles.noBottomBorder]}
          >
            <Text type="regular" bold style={styles.actionText}>
              Change shipping & billing date
            </Text>
            <Icon name="caret-right" style={styles.arrowIcon} />
          </TouchableOpacity>
        </>
      )}

      {subscription.status === SubscriptionStatus.PastDue && showBillingFields && (
        <View style={styles.pastDueMessage}>
          <Icon style={styles.alertIcon} name="warning" />
          <Text type="regular" style={styles.message}>
            The latest payment on this subscription was not successful and is now past due. Please
            update your method of payment below.
          </Text>
        </View>
      )}

      {showBillingFields && (
        <>
          <Divider appearance="large" />
          <PaymentMethodCard
            paymentMethod={paymentMethod}
            style={styles.paymentMethod}
            onEditPaymentMethodPress={onEditPaymentMethodPress}
          />
          <Divider appearance="large" />
          <AddressForm
            addressType={addressType}
            address={address}
            onEditAddressPress={onEditAddressPress}
          />
          <Divider appearance="large" />
        </>
      )}

      {showTrialUpgrade && (
        <TouchableOpacity
          onPress={onTrialUpgradePress}
          accessibilityLabel="Upgrade"
          style={styles.field}
        >
          <Text type="regular" bold style={styles.actionText}>
            Upgrade
          </Text>
          <Icon name="caret-right" style={styles.arrowIcon} />
        </TouchableOpacity>
      )}

      {showBillingFields && (
        <TouchableOpacity
          onPress={onViewStripeInvoicesPress}
          accessibilityLabel="View Invoices"
          style={styles.field}
        >
          <Text type="regular" bold style={styles.actionText}>
            View Invoices
          </Text>
          <Icon name="caret-right" style={styles.arrowIcon} />
        </TouchableOpacity>
      )}

      {showBillingFields && !isFreeSoftwareOnlySubscription(subscription.primaryProduct) && (
        <ProgramDetailsActions subscription={subscription} />
      )}

      {onManageButtonPress && (
        <TouchableOpacity
          onPress={onManageButtonPress}
          accessibilityLabel={manageButtonLabel}
          style={[styles.field, styles.noBottomBorder]}
        >
          <Text type="regular" bold style={styles.actionText}>
            {manageButtonLabel}
          </Text>
          <Icon name="caret-right" style={styles.arrowIcon} />
        </TouchableOpacity>
      )}
    </View>
  )
}

const themedStyle = StyleService.create({
  container: {
    flex: 1,
    paddingTop: 16,
    backgroundColor: 'theme.background',
  },
  field: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 64,
    marginHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 2,
    borderBottomColor: 'theme.surface.base2',
  },
  fieldSection: {
    flex: 1,
    justifyContent: 'space-between',
    height: '100%',
  },
  label: {
    color: 'theme.text.secondary',
  },
  billingInterval: {
    flexShrink: 1,
    flexWrap: 'wrap',
    marginRight: 16,
  },
  actionText: {
    color: 'theme.text.link',
  },
  arrowIcon: {
    width: 16,
    height: 16,
    color: 'theme.surface.base0',
  },
  pastDueMessage: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
  },
  alertIcon: {
    width: 24,
    height: 24,
    marginRight: 16,
    color: 'theme.warning.base',
  },
  message: {
    flex: 1,
  },
  noBottomBorder: {
    borderBottomWidth: 0,
  },
  paymentMethod: {
    paddingHorizontal: 16,
  },
  fieldDividerInset: {
    paddingHorizontal: 16,
  },
})
